<script setup lang="ts">
import { onMounted } from 'vue'
import { NEmpty, NSpin, NTag, NTime } from 'naive-ui'
import RouteShell from './RouteShell.vue'
import { useExpertConsultationsStore } from '../stores/expertConsultations'
import { useDocumentTitle } from '../composables/useDocumentTitle'

// Admin-only read-only log of expert-pool consultations. Rows where the
// expert did not record a memory are highlighted. Auth gating is router-level.

const store = useExpertConsultationsStore()

useDocumentTitle('Expert consultations')

onMounted(async () => {
  try {
    await store.fetchAll()
  } catch {
    // loadError surfaces in the template.
  }
})

function cost(cents: number | null): string {
  return cents === null ? '—' : `$${(cents / 100).toFixed(2)}`
}

function duration(ms: number | null): string {
  return ms === null ? '—' : `${(ms / 1000).toFixed(1)}s`
}
</script>

<template>
  <RouteShell>
    <section class="max-w-4xl mx-auto flex flex-col gap-4 pb-6">
      <header class="flex flex-col gap-1">
        <h1 class="text-2xl font-semibold text-white m-0">Expert consultations</h1>
        <p class="text-sm text-white/60 m-0">
          Recent expert-pool asks, newest first. Rows flagged <strong>no memory</strong> finished without the expert
          recording what it learned.
        </p>
      </header>

      <p v-if="store.loadError" class="text-sm text-red-300 m-0" data-testid="consultations-error">
        {{ store.loadError }}
      </p>
      <div v-else-if="!store.loaded" class="flex items-center justify-center py-12">
        <NSpin />
      </div>
      <NEmpty v-else-if="store.consultations.length === 0" description="No consultations yet." />
      <ul v-else class="flex flex-col gap-2 list-none p-0 m-0" data-testid="consultation-list">
        <li
          v-for="c in store.consultations"
          :key="c.id"
          :data-testid="`consultation-${c.id}`"
          :data-memory-written="c.memory_written"
          class="flex flex-col gap-1 rounded border px-3 py-2"
          :class="c.memory_written ? 'border-white/10 bg-black/20' : 'border-amber-400/60 bg-amber-500/10'"
        >
          <div class="flex items-center gap-2 flex-wrap text-sm text-white">
            <span class="font-medium">{{ c.role }}</span>
            <span v-if="c.repo" class="text-white/60">{{ c.repo }}</span>
            <NTag size="small">{{ c.mode }}</NTag>
            <NTag size="small">{{ c.status }}</NTag>
            <NTag v-if="!c.memory_written" size="small" type="warning" data-testid="no-memory-flag">no memory</NTag>
          </div>
          <div class="text-sm text-white/80">{{ c.question_excerpt }}</div>
          <div class="text-xs text-white/50">
            {{ cost(c.cost_cents) }} · {{ duration(c.duration_ms) }} ·
            <NTime :time="new Date(c.created_at)" type="relative" />
          </div>
        </li>
      </ul>
    </section>
  </RouteShell>
</template>
